import { formatDate } from "common";
import { Dayjs } from "dayjs";
import { compact, isNil, memoize, omit, unionBy } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { AllowedPrivacy, DisplayType, PurchaseItemPrivacy, Timeslot } from "types";
import { createPurchaseStoreToPayload, updatePurchaseData, useUpdatePurchase } from "../PurchaseManager";
import { queryClient } from "@/api/queryClient";
import {
    getCurrentMonthRange,
    getFirstBookableTimeslot,
    getMonthRange,
    TimeslotFilterOptions,
} from "@/components/DatePicker/ArrivalPickerHelper";
import { getArrivalData } from "@/components/DatePicker/useArrivalTimeslots";
import { flattenInfiniteData, generateQueryKey } from "@/hooks/helper";
import { useSeller } from "@/hooks/useAccount";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";

interface useArrivalTimeslotsProps {
    product: ID;
    quantity: number;
    start: Dayjs;
    showAllTimeslots?: boolean;
    isPastAvailabilityChecked?: boolean;
    isOverrideFutureCutoffChecked?: boolean;
    privacy?: AllowedPrivacy | PurchaseItemPrivacy;
    timezone?: string;
    filterOptions?: TimeslotFilterOptions;
    isEquipmentBased?: boolean;
}

const cacheTime = 120_000; // 2 minutes
const staleTime = 30_000; // 30 seconds

const queryOptions = {
    keepPreviousData: true,
    staleTime: staleTime,
    cacheTime: cacheTime,
};

const memoizedUnionBy = memoize((currentData, nextData, followingData) => {
    return unionBy(currentData, nextData, followingData, (slot: Timeslot) => slot.id);
});

export const useArrivalTimeslots = (props: useArrivalTimeslotsProps) => {
    const {
        product,
        quantity,
        showAllTimeslots,
        isPastAvailabilityChecked = false,
        isOverrideFutureCutoffChecked = false,
        privacy,
        timezone,
        start,
        filterOptions,
        isEquipmentBased = false,
    } = props;

    const seller = useSeller();
    const { mutateAsync: updatePurchase } = useUpdatePurchase();

    const formattedStart = formatDate(start);

    const { datepickerPreference: datePickerPreference } = seller.preferences;
    const needsUnits = datePickerPreference.displayType === DisplayType.PRICE;
    const currentRange = useMemo(() => getCurrentMonthRange(formattedStart), [formattedStart]);
    const nextRange = useMemo(() => getMonthRange(formattedStart, 1), [formattedStart]);
    const followingRange = useMemo(() => getMonthRange(formattedStart, 2), [formattedStart]);
    const [isFirstCallNotFinished, setIsFirstCallNotFinished] = useState(true);

    const startDate = isPastAvailabilityChecked ? formatDate(start.startOf("month")) : currentRange.start;
    const endDate = isPastAvailabilityChecked ? formatDate(start.endOf("month")) : currentRange.end;

    const startParam = [startDate, endDate].join(",");

    const currentPayload = useMemo(() => {
        const payloadObject: any = {
            start: startParam,
            product,
            limit: 500,
        };
        const include = ["capacity.max"];

        if (needsUnits) {
            include.push("units");
        }

        if (isEquipmentBased) {
            include.push("units.open");
        }

        if (product) payloadObject.include = include.join(",");

        if (privacy) {
            payloadObject.privacy = privacy;
        }

        if (!showAllTimeslots && !isPastAvailabilityChecked && quantity - 1 >= 0) {
            payloadObject["capacity_open[gt]"] = quantity - 1;
        }

        if (isPastAvailabilityChecked) {
            payloadObject.allowPastSlots = true;
        }

        if (isOverrideFutureCutoffChecked) {
            payloadObject.overrideFutureCutoff = true;
        }

        if (timezone) {
            payloadObject.timezone = timezone;
        }

        return payloadObject;
    }, [
        product,
        needsUnits,
        startParam,
        quantity,
        privacy,
        timezone,
        isPastAvailabilityChecked,
        isOverrideFutureCutoffChecked,
        showAllTimeslots,
        isEquipmentBased,
    ]);

    const nextPayload = useMemo(() => {
        const payload = {
            ...currentPayload,
            start: `${nextRange.start},${nextRange.end}`,
        };
        return payload;
    }, [currentPayload, nextRange]);

    const followingPayload = useMemo(() => {
        const payload = {
            ...currentPayload,
            start: `${followingRange.start},${followingRange.end}`,
        };
        return payload;
    }, [currentPayload, followingRange]);

    const currentQuery = useAutoFetchCollection<Timeslot>("timeslots", currentPayload, {
        ...queryOptions,
        queryKey: getTimeslotKey(currentPayload, product, isPastAvailabilityChecked),
        onSuccess: async (data) => {
            const pkgId = useCreatePurchaseStore.getState().getPurchasePackageId();
            if (pkgId) {
                setIsFirstCallNotFinished(false);
                return;
            }

            const hasPurchase = useCreatePurchaseStore.getState().purchaseId;
            const existingTimeslot = useCreatePurchaseStore.getState().getActiveItem().timeslot;
            if (!hasPurchase && !existingTimeslot && isFirstCallNotFinished) {
                const timeslots = flattenInfiniteData(data);
                const { activeItemIndex } = useCreatePurchaseStore.getState();
                const timeslot = getFirstBookableTimeslot(timeslots, isPastAvailabilityChecked, null, filterOptions);
                if (timeslot) {
                    useCreatePurchaseStore.getState().actions.setPurchase((draft) => {
                        const itemIndex = activeItemIndex;
                        if (draft.items[itemIndex]) {
                            draft.items[itemIndex].arrivalDate = timeslot.date;
                            draft.items[itemIndex].arrivalTime = isNil(timeslot.time) ? undefined : timeslot.time;
                            draft.items[itemIndex].timeslot = timeslot ?? null;
                        }
                    });

                    const payload = createPurchaseStoreToPayload(useCreatePurchaseStore.getState().purchase);
                    try {
                        useCreatePurchaseStore.setState({ isProcessing: true });
                        const newPurchase = await updatePurchase(payload);
                        updatePurchaseData(newPurchase);
                    } catch {
                        console.error("Error creating purchase from ArrivalPicker");
                    }
                }
            }

            setIsFirstCallNotFinished(false);
        },
    });

    const nextQuery = useAutoFetchCollection<Timeslot>("timeslots", nextPayload, {
        ...queryOptions,
        queryKey: getTimeslotKey(nextPayload, product, isPastAvailabilityChecked),
    });

    const followingQuery = useAutoFetchCollection<Timeslot>("timeslots", followingPayload, {
        ...queryOptions,
        queryKey: getTimeslotKey(followingPayload, product, isPastAvailabilityChecked),
    });

    const currentData = currentQuery.data;
    const nextData = nextQuery.data;
    const followingData = followingQuery.data;

    const areAllQueriesFinished = currentQuery.isSuccess && nextQuery.isSuccess && followingQuery.isSuccess;

    const combinedTimeslots = useMemo(() => {
        if (!areAllQueriesFinished) {
            return [];
        }

        const result = memoizedUnionBy(currentData, nextData, followingData);
        return result;
    }, [currentData, nextData, followingData, areAllQueriesFinished]);

    const customKey = getTimeslotKeyForCombinedData(currentPayload, product, isPastAvailabilityChecked);

    const customTimeslotData = getArrivalData(product, customKey);

    const allQueriesSuccessful = currentQuery.isSuccess;

    const shouldMergeTimeslots = !isFirstCallNotFinished || allQueriesSuccessful;

    useEffect(() => {
        if (!shouldMergeTimeslots) return;

        const map = new Map<string, Timeslot>();

        for (const timeslot of combinedTimeslots) {
            map.set(getKey(timeslot), timeslot);
        }

        for (const timeslot of customTimeslotData ?? []) {
            map.set(getKey(timeslot), timeslot);
        }

        const final = [...map.values()];
        queryClient.setQueryData(customKey, final);
        setTimeout(() => {
            queryClient.removeQueries(customKey);
        }, cacheTime);
    }, [shouldMergeTimeslots, combinedTimeslots, customTimeslotData, customKey]);

    return {
        data: shouldMergeTimeslots ? combinedTimeslots ?? customTimeslotData ?? [] : [],

        isLoading: currentQuery.isLoading || nextQuery.isLoading || followingQuery.isLoading,

        isFetching: currentQuery.isFetching || nextQuery.isFetching || followingQuery.isFetching,

        hasNextPage: currentQuery.hasNextPage ?? nextQuery.hasNextPage ?? followingQuery.hasNextPage,

        error: currentQuery.error ?? nextQuery.error ?? followingQuery.error,

        isFetchingNextPage:
            currentQuery.isFetchingNextPage || nextQuery.isFetchingNextPage || followingQuery.isFetchingNextPage,
    };
};

const getKey = (t: Timeslot) => `${t.arrivalDatetime?.valueOf()}_${t.privacy}_${t.product}_${t.canBook}`;

const getTimeslotKey = (payload: any, productId: ID, shouldDisablePastSlots: boolean) => {
    return generateQueryKey(
        ["timeslots", "collection", "arrival", "individual", payload.privacy, productId],
        omit(payload, ["product", "capacity_open[gt]", shouldDisablePastSlots ? "allowPastSlots" : ""]),
    );
};

const getTimeslotKeyForCombinedData = (payload: any, productId: ID, shouldDisablePastSlots: boolean) => {
    const keysToOmit = ["product", "start", "capacity_open[gt]"];

    if (shouldDisablePastSlots) {
        keysToOmit.push("allowPastSlots");
    }

    return generateQueryKey(
        ["timeslots", "collection", "arrival", payload.privacy, productId],
        omit(payload, keysToOmit),
    );
};

export const eraseArrivalCacheForCreatePurchase = (productId: ID, privacy: AllowedPrivacy) => {
    console.debug(`🚨 Deleted data for ${productId}`);
    queryClient.removeQueries(compact(["timeslots", "collection", "arrival", privacy, productId]));
    queryClient.removeQueries(compact(["timeslots", "collection", "upcoming", productId]));
};

export const invalidateCacheForIndividualTimeslotRequests = (productId: ID, privacy: AllowedPrivacy) => {
    queryClient.invalidateQueries(["timeslots", "collection", "arrival", "individual", privacy, productId]);
};
